import { createContext, type ReactNode, useContext, useState } from 'react'
import type {
  RedactedTextsState,
  RedactedTextsWithCaseId,
  RedactText,
  RedactTextType,
} from './redactionConstants'
import { redactText } from './redactText'

type RedactionProviderContextProps = {
  isRedactionActive: boolean
  getRedactedTexts: () => RedactedTextsWithCaseId
  texts: RedactedTextsState
  redactText: (textType: RedactTextType, text: RedactText, defaultText: string) => void
  startRedaction: () => void
  cancelRedaction: () => void
}

type RedactionProviderProps = {
  children: (props: RedactionProviderContextProps) => ReactNode
  caseId: string
}

const defaultValues: RedactedTextsState = {
  moreInfo: '',
  sender: '',
  formItems: {},
  chatMessages: {},
}

const RedactionContext = createContext<RedactionProviderContextProps>({
  isRedactionActive: false,
  getRedactedTexts: () => ({ ...defaultValues, caseId: '' }),
  texts: defaultValues,
  redactText: () => null,
  startRedaction: () => null,
  cancelRedaction: () => null,
})

export const RedactionProvider = ({ children, caseId }: RedactionProviderProps) => {
  const [isRedactionActive, setIsRedactionActive] = useState<boolean>(false)
  const [redactedTexts, setRedactedTexts] = useState<RedactedTextsState>({})

  const values: RedactionProviderContextProps = {
    texts: redactedTexts,
    isRedactionActive,
    getRedactedTexts: (): RedactedTextsWithCaseId => ({ ...redactedTexts, caseId: caseId }),
    startRedaction: () => {
      setIsRedactionActive(true)
    },
    cancelRedaction: () => {
      setIsRedactionActive(false)
      setRedactedTexts({})
    },
    redactText: (textType: RedactTextType, text: RedactText, defaultText: string) => {
      setRedactedTexts(prevState => redactText(prevState, textType, text, defaultText))
    },
  }

  return <RedactionContext.Provider value={values}>{children(values)}</RedactionContext.Provider>
}

export const useRedaction = () => useContext(RedactionContext)
